import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Carregando from "../componentes/Carregando";
import { Cabine } from "../componentes/FerramentaUi";
import { mensagemDaAbertura, type MotivoAbertura } from "../lib/abrir";
import {
  ChipsDaClassificacao,
  Chip,
  Codigo,
  Estrela,
  Fato,
  GovForaDoAr,
  ReguaDeReducao,
  SemResultado,
} from "../componentes/CstUi";
import {
  alternarFavorito,
  anotarFavorito,
  lerCatalogo,
  lerFavoritos,
  TRIBUTOS,
  type Favorito,
  type Tributo,
} from "../lib/cstClassTrib";
import {
  chaveDoFavorito,
  classificacoesEmTexto,
  cstsEmTexto,
  FILTROS_VAZIOS,
  filtrar,
  hojeEmIso,
  montarTabela,
  percentualEmTexto,
  siglasDeDfe,
  temFiltroLigado,
  type Classificacao,
  type Filtros,
  type Tabela,
} from "../lib/tabelaCst";

// CST e ClassTrib (000223) — a tabela de situações e classificações tributárias
// da Reforma, da Pós RT.
//
// ---------------------------------------------------------------------------
// O catálogo é da RECEITA, e não do hub
// ---------------------------------------------------------------------------
// A ferramenta de origem carrega um `cst-data.json` de 103 KB. Ele não foi
// portado, e a medição de 14/09 diz por quê: ele tem 17 CSTs contra 18 da
// Receita, 130 classificações contra 164, 19 descrições desatualizadas e 8
// códigos que a Receita não tem em data nenhuma.
//
// E o catálogo MUDA com a data — 164 classificações em 2026-01-01, 161 em
// 2026-06-01. Por isso a data é um campo da cabine, e não um detalhe escondido:
// é a diferença entre "o que vale" e "o que valia".
//
// ---------------------------------------------------------------------------
// Os segmentos e a query
// ---------------------------------------------------------------------------
//   /ferramenta/cst-e-classtrib             a tabela
//   /ferramenta/cst-e-classtrib/favoritos   o que o aluno guardou
//
// A classificação escolhida, o tributo e a data viajam na QUERY
// (`?cl=200019&t=cbs-ibs&data=2026-06-01`), e não no caminho: "me manda o
// 200019" é exatamente o que um contador manda ao colega, e o botão de voltar
// tem de desfazer a escolha em vez de sair da ferramenta. É a decisão do Mapa de
// Concursos; os FAVORITOS, esses, são seção com endereço próprio.

export const BASE = "/ferramenta/cst-e-classtrib";

type Estado =
  | { fase: "carregando" }
  | { fase: "pronto"; tabela: Tabela }
  | { fase: "recusado"; motivo: MotivoAbertura }
  | { fase: "indisponivel"; motivo: string };

export default function CstClassTrib() {
  const params = useParams();
  const [query, setQuery] = useSearchParams();
  const resto = (params["*"] ?? "").split("/").filter(Boolean);
  const emFavoritos = resto[0] === "favoritos";

  const tributo: Tributo = query.get("t") === "is" ? "is" : "cbs-ibs";
  const data = query.get("data") ?? hojeEmIso();
  const escolhida = query.get("cl") ?? "";

  const [estado, setEstado] = useState<Estado>({ fase: "carregando" });
  const [favoritos, setFavoritos] = useState<Favorito[] | null>(null);
  const [filtros, setFiltros] = useState<Filtros>(FILTROS_VAZIOS);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [erroEm, setErroEm] = useState<string | null>(null);
  const [recarregar, setRecarregar] = useState(0);
  /** Os grupos que o aluno abriu à mão. Com filtro ligado, todos abrem sozinhos. */
  const [abertos, setAbertos] = useState<Set<string>>(new Set());

  // A chamada em voo. Sem isto, a resposta lenta de uma data ANTERIOR chega
  // depois da atual e sobrescreve o catálogo certo pelo velho — defeito
  // invisível e intermitente, que aparece justamente com internet ruim.
  const rodada = useRef(0);

  useEffect(() => {
    const minha = ++rodada.current;
    setEstado({ fase: "carregando" });
    void lerCatalogo(tributo, data).then((r) => {
      if (minha !== rodada.current) return;
      if (r.situacao === "ok") {
        setEstado({ fase: "pronto", tabela: montarTabela(r.dados.csts, r.dados.fichas) });
      } else if (r.situacao === "sem-permissao") {
        setEstado({ fase: "recusado", motivo: r.motivo });
      } else {
        setEstado({ fase: "indisponivel", motivo: r.motivo });
      }
    });
  }, [tributo, data, recarregar]);

  useEffect(() => {
    let vivo = true;
    void lerFavoritos().then((r) => {
      if (vivo && r !== null && "favoritos" in r) setFavoritos(r.favoritos);
    });
    return () => {
      vivo = false;
    };
  }, []);

  const marcados = useMemo(
    () =>
      new Set(
        (favoritos ?? []).map((f) => chaveDoFavorito(f.tributo, f.cst, f.cclasstrib)),
      ),
    [favoritos],
  );

  function mudarQuery(parte: Record<string, string | null>) {
    const q = new URLSearchParams(query);
    for (const [k, v] of Object.entries(parte)) {
      if (v === null || v === "") q.delete(k);
      else q.set(k, v);
    }
    setQuery(q, { replace: true });
  }

  async function alternar(t: Tributo, cst: string, cl: string | null) {
    const chave = chaveDoFavorito(t, cst, cl);
    setSalvando(chave);
    setErroEm(null);
    const r = await alternarFavorito(t, cst, cl);
    setSalvando(null);

    if (r === null || "motivo" in r) {
      // Erro de UM tique fica NA LINHA. Trocar a tela inteira por "não
      // conseguimos carregar" apagaria a tabela, que está certa — a cicatriz do
      // `alternarFeito` do Calendário.
      setErroEm(chave);
      return;
    }
    // A lista vem do BANCO, e não de um palpite local: quem sabe o estado é ele
    // (com duas abas abertas, a tela erraria).
    const atual = await lerFavoritos();
    if (atual !== null && "favoritos" in atual) setFavoritos(atual.favoritos);
  }

  if (estado.fase === "recusado") {
    const m = mensagemDaAbertura(estado.motivo);
    return (
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="rounded-cf-lg border border-borda bg-superficie p-6">
          <h1 className="font-display text-xl font-bold text-titulo">{m.titulo}</h1>
          <p className="mt-2 text-sm leading-relaxed text-cinza">{m.texto}</p>
        </div>
      </main>
    );
  }

  const tabela = estado.fase === "pronto" ? estado.tabela : null;
  const resultado = tabela
    ? filtrar(tabela, filtros, marcados, tributo)
    : { csts: [], quantas: 0, quantosCsts: 0 };
  const siglas = tabela ? siglasDeDfe(tabela) : [];
  const aberta = tabela
    ? resultado.csts.flatMap((c) => c.classificacoes).find((c) => c.codigo === escolhida) ??
      tabela.csts.flatMap((c) => c.classificacoes).find((c) => c.codigo === escolhida) ??
      null
    : null;

  return (
    <main className="mx-auto max-w-6xl px-4 py-7 sm:px-6">
      <Cabine
        rotulo="Consulta"
        titulo="CST e ClassTrib"
        descricao={
          <>
            As situações tributárias (CST) e as classificações (cClassTrib) da Reforma,{" "}
            <strong className="text-white">direto da base da Receita Federal</strong> — não é uma
            cópia: o catálogo muda com a norma, e a data abaixo escolhe qual versão você vê.
          </>
        }
        fatos={
          tabela ? (
            <>
              <TileNavy rotulo="Situações tributárias" valor={String(tabela.csts.length)} apoio="CSTs" />
              <TileNavy
                rotulo="Classificações"
                valor={String(tabela.totalClassificacoes)}
                apoio="cClassTrib"
              />
              <TileNavy
                rotulo="Seus favoritos"
                valor={String((favoritos ?? []).length)}
                apoio={
                  <Link to={`${BASE}/favoritos${window.location.search}`} className="underline hover:text-white">
                    ver a lista
                  </Link>
                }
              />
            </>
          ) : undefined
        }
        rodape={
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-[1fr_11rem]">
              <div>
                <span className="mb-1.5 block text-xs font-bold tracking-widest text-navy-mute uppercase">
                  Buscar
                </span>
                <input
                  id="cst-busca"
                  type="search"
                  autoFocus
                  className="h-12 w-full rounded-xl border border-navy-mute/30 bg-white/5 px-4 text-base text-white placeholder:text-navy-mute/70 focus:border-laranja focus:ring-3 focus:ring-laranja/25 focus:outline-none"
                  placeholder="cesta básica, 200019, 410, exportação…"
                  value={filtros.termo}
                  onChange={(e) => setFiltros((f) => ({ ...f, termo: e.target.value }))}
                />
              </div>
              <div>
                <span className="mb-1.5 block text-xs font-bold tracking-widest text-navy-mute uppercase">
                  Vigência em
                </span>
                <input
                  id="cst-data"
                  type="date"
                  className="h-12 w-full rounded-xl border border-navy-mute/30 bg-white/5 px-3 text-base text-white focus:border-laranja focus:ring-3 focus:ring-laranja/25 focus:outline-none"
                  value={data}
                  onChange={(e) => mudarQuery({ data: e.target.value, cl: null })}
                />
              </div>
            </div>

            {/* As duas tabelas da Receita. Não são abas de apresentação: são
                catálogos diferentes, e o código 000001 existe nos dois com
                significados distintos. */}
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Tributo">
              {TRIBUTOS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tributo === t.id}
                  title={t.resumo}
                  onClick={() => mudarQuery({ t: t.id === "cbs-ibs" ? null : t.id, cl: null })}
                  className={
                    // A aba escolhida tem fundo BRANCO, então a tinta dela é
                    // `azul` — cor de marca, escura, 14,57 : 1.
                    //
                    // Nasceu com `text-navy-ink` e o rótulo SUMIU: `#f1f1f2` é a
                    // tinta CLARA para escrever SOBRE o navy, não a cor do navy,
                    // e sobre branco ele dá **1,13 : 1** (medido em 14/09, na
                    // captura). O nome do token engana quem lê depressa, e a
                    // outra frente caiu na mesma armadilha no mesmo dia, na
                    // Base de Cálculo — lá quem viu foi Victor na tela.
                    //
                    // Cor FIXA é o certo: dentro do território navy, cor
                    // semântica é a armadilha central desta casa.
                    tributo === t.id
                      ? "rounded-xl bg-white px-4 py-2 text-sm font-bold text-azul"
                      : "rounded-xl border border-navy-mute/40 px-4 py-2 text-sm font-semibold text-white transition hover:border-laranja"
                  }
                >
                  {t.nome}
                </button>
              ))}
            </div>
          </div>
        }
      />

      {emFavoritos ? (
        <PainelDeFavoritos
          favoritos={favoritos}
          tabela={tabela}
          tributo={tributo}
          salvando={salvando}
          erroEm={erroEm}
          aoAlternar={alternar}
          aoAnotar={async (id, nota) => {
            const ok = await anotarFavorito(id, nota);
            if (!ok) return false;
            const atual = await lerFavoritos();
            if (atual !== null && "favoritos" in atual) setFavoritos(atual.favoritos);
            return true;
          }}
        />
      ) : (
        <>
          <BarraDeFiltros
            filtros={filtros}
            siglas={siglas}
            resultado={resultado}
            tabela={tabela}
            aoMudar={(p) => setFiltros((f) => ({ ...f, ...p }))}
            aoLimpar={() => setFiltros(FILTROS_VAZIOS)}
          />

          {estado.fase === "indisponivel" ? (
            <div className="mt-5">
              <GovForaDoAr motivo={estado.motivo} aoTentar={() => setRecarregar((n) => n + 1)} />
            </div>
          ) : estado.fase === "carregando" ? (
            <Carregando />
          ) : resultado.quantas === 0 ? (
            <div className="mt-5">
              <SemResultado aoLimpar={() => setFiltros(FILTROS_VAZIOS)} />
            </div>
          ) : (
            <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_23rem]">
              <Lista
                resultado={resultado}
                escolhida={escolhida}
                marcados={marcados}
                tributo={tributo}
                salvando={salvando}
                erroEm={erroEm}
                buscando={temFiltroLigado(filtros)}
                abertos={abertos}
                aoAbrir={(cst) =>
                  setAbertos((s) => {
                    const novo = new Set(s);
                    if (novo.has(cst)) novo.delete(cst);
                    else novo.add(cst);
                    return novo;
                  })
                }
                aoEscolher={(cl) => mudarQuery({ cl })}
                aoAlternar={alternar}
              />
              <div className="lg:sticky lg:top-24 lg:self-start">
                <Ficha
                  c={aberta}
                  tributo={tributo}
                  data={data}
                  marcados={marcados}
                  salvando={salvando}
                  aoAlternar={alternar}
                />
              </div>
            </div>
          )}

          {tabela && tabela.semFicha.length > 0 && (
            // As duas listas do gov discordam, e a tela DIZ quantas. Escolher uma
            // em silêncio seria decidir por ele o que a própria Receita não
            // decidiu.
            <p className="mt-6 rounded-xl bg-superficie-2 p-4 text-xs leading-relaxed text-cinza">
              <strong className="text-tinta">
                {tabela.semFicha.length === 1
                  ? "1 classificação está sem ficha nesta data"
                  : `${tabela.semFicha.length} classificações estão sem ficha nesta data`}
              </strong>{" "}
              ({tabela.semFicha.join(", ")}). A Receita ainda as lista dentro do CST, mas não
              publica os detalhes delas na data escolhida — elas aparecem aqui marcadas, em vez de
              sumir. Trocar a vigência acima costuma resolver.
            </p>
          )}
        </>
      )}

      <p className="mt-8 rounded-xl bg-superficie-2 p-4 text-xs leading-relaxed text-cinza">
        Os dados vêm da base de tributos sobre o consumo da Receita Federal, em{" "}
        <strong className="text-tinta">ambiente de produção restrita</strong> (piloto), e mudam
        conforme a norma evolui. Confirme na LC 214/2025 e nos atos da Receita antes de usar um
        código em documento fiscal.
      </p>
    </main>
  );
}

function TileNavy({
  rotulo,
  valor,
  apoio,
}: {
  rotulo: string;
  valor: string;
  apoio: React.ReactNode;
}) {
  return (
    <div className="rounded-cf-lg bg-white/10 p-4">
      <p className="text-xs font-bold tracking-widest text-navy-mute uppercase">{rotulo}</p>
      <p className="mt-1 font-display text-2xl font-bold text-white tabular-nums">{valor}</p>
      <p className="mt-0.5 text-xs text-navy-mute">{apoio}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// A barra de filtros
// ---------------------------------------------------------------------------
function BarraDeFiltros({
  filtros,
  siglas,
  resultado,
  tabela,
  aoMudar,
  aoLimpar,
}: {
  filtros: Filtros;
  siglas: string[];
  resultado: { quantas: number; quantosCsts: number };
  tabela: Tabela | null;
  aoMudar: (p: Partial<Filtros>) => void;
  aoLimpar: () => void;
}) {
  const ligado = temFiltroLigado(filtros);
  const botao = (chave: keyof Filtros, texto: string, dica: string) => (
    <button
      key={chave}
      type="button"
      aria-pressed={filtros[chave] === true}
      title={dica}
      onClick={() => aoMudar({ [chave]: !filtros[chave] } as Partial<Filtros>)}
      className={
        filtros[chave] === true
          ? "rounded-full bg-acento px-3.5 py-1.5 text-xs font-bold text-acento-tinta"
          : "rounded-full border border-borda px-3.5 py-1.5 text-xs font-semibold text-cinza transition hover:border-acento hover:text-acento"
      }
    >
      {texto}
    </button>
  );

  return (
    <section className="mt-5 rounded-cf-lg border border-borda bg-superficie p-4">
      <div className="flex flex-wrap items-center gap-2">
        {botao("comReducao", "Com redução", "Só as que reduzem a alíquota do IBS ou da CBS")}
        {botao("comCredito", "Dá crédito", "Só as em que o adquirente se apropria do crédito")}
        {botao("comCreditoPresumido", "Crédito presumido", "Só as que geram crédito presumido")}
        {botao("soFavoritos", "Meus favoritos", "Só o que você marcou")}

        <label className="ml-auto flex items-center gap-2">
          <span className="text-xs font-semibold text-cinza">Documento</span>
          <select
            id="cst-dfe"
            className="h-9 rounded-lg border border-borda bg-superficie px-2 text-xs text-tinta focus:border-laranja focus:ring-2 focus:ring-laranja/20 focus:outline-none"
            value={filtros.dfe}
            onChange={(e) => aoMudar({ dfe: e.target.value })}
          >
            {/* O combo só oferece as siglas que EXISTEM no catálogo desta data —
                opção que leva a lista vazia é ruído. E sem contagem ao lado: os
                números dos combos saíram do balcão em 09/09 porque não se
                recalculavam sob os outros filtros. */}
            <option value="">Todos</option>
            {siglas.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-borda pt-3">
        <p className="text-sm text-cinza" data-contagem={resultado.quantas}>
          <strong className="text-tinta">{classificacoesEmTexto(resultado.quantas)}</strong>
          {" em "}
          {cstsEmTexto(resultado.quantosCsts)}
          {/* O total da base ao lado do recorte: lista filtrada sem o total vira
              "não existe" na cabeça de quem procura. */}
          {tabela && ligado && (
            <span className="text-cinza">
              {" "}
              · de {tabela.totalClassificacoes} no catálogo
            </span>
          )}
        </p>
        {ligado && (
          <button
            type="button"
            onClick={aoLimpar}
            className="text-xs font-semibold text-link hover:underline"
          >
            limpar os filtros
          </button>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// A lista
// ---------------------------------------------------------------------------
function Lista({
  resultado,
  escolhida,
  marcados,
  tributo,
  salvando,
  erroEm,
  buscando,
  abertos,
  aoAbrir,
  aoEscolher,
  aoAlternar,
}: {
  resultado: { csts: { codigo: string; descricao: string; classificacoes: Classificacao[] }[] };
  escolhida: string;
  marcados: Set<string>;
  tributo: Tributo;
  salvando: string | null;
  erroEm: string | null;
  /** Há filtro ligado: aí os grupos que sobraram abrem sozinhos. */
  buscando: boolean;
  abertos: Set<string>;
  aoAbrir: (cst: string) => void;
  aoEscolher: (codigo: string) => void;
  aoAlternar: (t: Tributo, cst: string, cl: string | null) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      {resultado.csts.map((cst) => {
        const chaveCst = chaveDoFavorito(tributo, cst.codigo, null);
        // Os grupos nascem FECHADOS, e abrem sozinhos quando há filtro.
        //
        // Medido em 14/09: com os 18 CSTs abertos, a tela tem **20.541px** —
        // 22,8 telas de rolagem para 164 classificações. É a cicatriz do IR Pro
        // (8.249px → 1.120px com o MESMO conteúdo) e da busca de CNAE (8.521px)
        // pela terceira vez, e aqui é pior porque a lista é maior.
        //
        // Mas fechar tudo com busca ativa seria o defeito oposto: o aluno digita
        // "cesta", vê "1 classificação em 1 CST" e não vê a classificação. Com
        // filtro, o que sobrou É o resultado — então abre.
        const aberto = buscando || abertos.has(cst.codigo) || cst.classificacoes.some((c) => c.codigo === escolhida);
        return (
          <section
            key={cst.codigo}
            data-cst={cst.codigo}
            data-aberto={aberto ? "sim" : "nao"}
            className="overflow-hidden rounded-cf-lg border border-borda bg-superficie"
            aria-labelledby={`cst-${cst.codigo}`}
          >
            {/* O cabeçalho do grupo: o CST é o CAPÍTULO, e a classificação é o
                item. A origem punha os dois na mesma tabela plana, com seis
                colunas de bolinhas — e a hierarquia, que é o conteúdo, sumia. */}
            <header
              className={`flex flex-wrap items-center gap-3 px-4 py-3 ${
                aberto ? "border-b border-borda bg-superficie-2" : ""
              }`}
            >
              <button
                type="button"
                aria-expanded={aberto}
                aria-controls={`lista-${cst.codigo}`}
                onClick={() => aoAbrir(cst.codigo)}
                className="flex min-w-0 flex-1 items-center gap-3 text-left"
              >
                <span
                  aria-hidden="true"
                  className={`shrink-0 text-cinza transition-transform ${aberto ? "rotate-90" : ""}`}
                >
                  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </span>
                <Codigo grande>{cst.codigo}</Codigo>
                <h2
                  id={`cst-${cst.codigo}`}
                  className="min-w-0 flex-1 font-display text-sm font-bold text-titulo"
                >
                  {cst.descricao}
                </h2>
              </button>
              <Chip>{classificacoesEmTexto(cst.classificacoes.length)}</Chip>
              <Estrela
                marcada={marcados.has(chaveCst)}
                ocupada={salvando === chaveCst}
                rotulo={`o CST ${cst.codigo} inteiro`}
                aoClicar={() => aoAlternar(tributo, cst.codigo, null)}
              />
            </header>

            {erroEm === chaveCst && (
              <p className="border-b border-borda px-4 py-2 text-xs text-erro-tinta">
                Não conseguimos salvar este favorito agora. O resto da tabela continua aqui.
              </p>
            )}

            <ul id={`lista-${cst.codigo}`} hidden={!aberto} className="divide-y divide-borda">
              {cst.classificacoes.map((c) => {
                const chave = chaveDoFavorito(tributo, c.cst, c.codigo);
                const ativa = c.codigo === escolhida;
                return (
                  <li key={c.codigo} data-classificacao={c.codigo}>
                    <div
                      role="button"
                      tabIndex={0}
                      aria-pressed={ativa}
                      onClick={() => aoEscolher(c.codigo)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          aoEscolher(c.codigo);
                        }
                      }}
                      className={`flex w-full cursor-pointer items-start gap-3 px-4 py-3 text-left transition ${
                        ativa ? "bg-acento/10" : "hover:bg-superficie-2"
                      }`}
                    >
                      <span className="mt-0.5">
                        <Codigo>{c.codigo}</Codigo>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm leading-snug text-tinta">{c.descricao}</span>
                        <span className="mt-1.5 block">
                          <ChipsDaClassificacao c={c} compacto />
                        </span>
                      </span>
                      <Estrela
                        marcada={marcados.has(chave)}
                        ocupada={salvando === chave}
                        rotulo={`a classificação ${c.codigo}`}
                        aoClicar={() => aoAlternar(tributo, c.cst, c.codigo)}
                      />
                    </div>
                    {erroEm === chave && (
                      <p className="px-4 pb-2 text-xs text-erro-tinta">
                        Não conseguimos salvar este favorito agora. Tente de novo — o resto da
                        tabela continua aqui.
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// A ficha
// ---------------------------------------------------------------------------
function Ficha({
  c,
  tributo,
  data,
  marcados,
  salvando,
  aoAlternar,
}: {
  c: Classificacao | null;
  tributo: Tributo;
  data: string;
  marcados: Set<string>;
  salvando: string | null;
  aoAlternar: (t: Tributo, cst: string, cl: string | null) => void;
}) {
  if (!c) {
    return (
      <div className="rounded-cf-lg border border-dashed border-borda bg-superficie p-6">
        <h2 className="font-display text-base font-bold text-titulo">A ficha completa</h2>
        <p className="mt-2 text-sm leading-relaxed text-cinza">
          Escolha uma classificação ao lado para ver o tipo de alíquota, a nomenclatura aplicável,
          os percentuais de redução, quem se apropria do crédito e em quais documentos fiscais ela
          vale.
        </p>
      </div>
    );
  }

  const chave = chaveDoFavorito(tributo, c.cst, c.codigo);
  const dfes = c.tiposDfeClassificacao ?? [];

  return (
    <div className="rounded-cf-lg border border-borda bg-superficie p-5" data-ficha={c.codigo}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-bold tracking-widest text-cinza uppercase">
            CST {c.cst} · classificação
          </p>
          <p className="mt-1">
            <Codigo grande>{c.codigo}</Codigo>
          </p>
        </div>
        <Estrela
          marcada={marcados.has(chave)}
          ocupada={salvando === chave}
          rotulo={`a classificação ${c.codigo}`}
          aoClicar={() => aoAlternar(tributo, c.cst, c.codigo)}
        />
      </div>

      <h2 className="mt-3 font-display text-base leading-snug font-bold text-titulo">
        {c.descricao}
      </h2>

      <div className="mt-3">
        <ChipsDaClassificacao c={c} />
      </div>

      {c.semFicha ? (
        <p className="mt-4 rounded-xl bg-alerta-fundo p-3 text-xs leading-relaxed text-alerta-tinta">
          A Receita lista este código dentro do CST {c.cst}, mas não publica a ficha dele na
          vigência de {data}. Os campos abaixo não existem para ele nesta data — troque a vigência
          na cabine para ver se ele aparece completo em outro período.
        </p>
      ) : (
        <>
          {/* Os percentuais vêm PRIMEIRO quando existem: é o número que decide a
              conduta, e a régua existe porque a comparação entre 30, 60 e 100
              é o conteúdo. Sem redução, o bloco inteiro não aparece — barra em
              zero repetida em 101 das 164 seria ruído com cara de informação. */}
          {(c.percentualReducaoCbs ?? 0) > 0 ||
          (c.percentualReducaoIbsUf ?? 0) > 0 ||
          (c.percentualReducaoIbsMun ?? 0) > 0 ? (
            <div className="mt-4 space-y-3 rounded-xl bg-superficie-2 p-3">
              <p className="text-xs font-bold tracking-widest text-cinza uppercase">
                Redução de alíquota
              </p>
              <ReguaDeReducao rotulo="CBS" percentual={c.percentualReducaoCbs} />
              <ReguaDeReducao rotulo="IBS estadual" percentual={c.percentualReducaoIbsUf} />
              <ReguaDeReducao rotulo="IBS municipal" percentual={c.percentualReducaoIbsMun} />
            </div>
          ) : null}

          <div className="mt-4">
            {c.descricaoTratamentoTributario && (
              <Fato rotulo="Tratamento tributário">{c.descricaoTratamentoTributario}</Fato>
            )}
            {c.tipoAliquota && <Fato rotulo="Tipo de alíquota">{c.tipoAliquota}</Fato>}
            {c.nomenclatura && <Fato rotulo="Nomenclatura aplicável">{c.nomenclatura}</Fato>}
            {c.creditoOperacaoAntecedente && (
              <Fato rotulo="Crédito da operação antecedente">{c.creditoOperacaoAntecedente}</Fato>
            )}
            <Fato rotulo="Crédito para o adquirente">
              {/* Os dois tributos separados, e não um "sim" agregado: uma
                  classificação pode dar crédito de CBS e não de IBS, e o
                  agregado esconderia exatamente a diferença que importa. */}
              {c.indicaApropriacaoCreditoAdquirenteCbs || c.indicaApropriacaoCreditoAdquirenteIbs
                ? [
                    c.indicaApropriacaoCreditoAdquirenteCbs ? "CBS" : null,
                    c.indicaApropriacaoCreditoAdquirenteIbs ? "IBS" : null,
                  ]
                    .filter(Boolean)
                    .join(" e ")
                : "não"}
            </Fato>
            {(c.percentualReducaoCbs ?? 0) === 0 &&
              (c.percentualReducaoIbsUf ?? 0) === 0 &&
              (c.percentualReducaoIbsMun ?? 0) === 0 && (
                <Fato rotulo="Redução de alíquota">
                  {percentualEmTexto(c.percentualReducaoCbs)}
                </Fato>
              )}
            {c.dataAtualizacao && (
              <Fato rotulo="Atualizada pela Receita em">
                {String(c.dataAtualizacao).slice(0, 10).split("-").reverse().join("/")}
              </Fato>
            )}
          </div>

          <div className="mt-4">
            <p className="text-xs font-bold tracking-widest text-cinza uppercase">
              Documentos em que vale
            </p>
            {dfes.length === 0 ? (
              // A ausência de uma linha não informa nada: sem esta frase, o
              // bloco vazio se lê como "vale em todos".
              <p className="mt-1.5 text-xs leading-relaxed text-cinza">
                A Receita não associa esta classificação a nenhum documento fiscal nesta data.
              </p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {dfes.map((d) => (
                  <Chip key={d.tipo} titulo={d.descricao}>
                    {d.sigla}
                  </Chip>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Os favoritos
// ---------------------------------------------------------------------------
function PainelDeFavoritos({
  favoritos,
  tabela,
  tributo,
  salvando,
  erroEm,
  aoAlternar,
  aoAnotar,
}: {
  favoritos: Favorito[] | null;
  tabela: Tabela | null;
  tributo: Tributo;
  salvando: string | null;
  erroEm: string | null;
  aoAlternar: (t: Tributo, cst: string, cl: string | null) => void;
  aoAnotar: (id: string, nota: string) => Promise<boolean>;
}) {
  if (favoritos === null) return <Carregando />;

  if (favoritos.length === 0) {
    return (
      <section className="mt-5 rounded-cf-lg border border-borda bg-superficie p-8 text-center">
        <h2 className="font-display text-lg font-bold text-titulo">
          Você ainda não guardou nenhuma
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-cinza">
          Clique na estrela ao lado de uma classificação — ou de um CST inteiro — para guardá-la
          aqui. Dá para anotar em cada uma o que ela significa no seu dia a dia.
        </p>
        <Link
          to={`${BASE}${window.location.search}`}
          className="pill-cta mt-5 inline-flex"
        >
          Abrir a tabela
        </Link>
      </section>
    );
  }

  // A descrição sai do CATÁLOGO carregado, e não do favorito: o que se guarda é
  // o CÓDIGO, então quando a Receita reescreve o texto o aluno vê a redação
  // nova. É por isso que um favorito do OUTRO tributo aparece sem descrição
  // aqui — o catálogo na tela é de um só.
  const porCodigo = new Map<string, Classificacao>();
  const nomeDoCst = new Map<string, string>();
  for (const cst of tabela?.csts ?? []) {
    nomeDoCst.set(cst.codigo, cst.descricao);
    for (const c of cst.classificacoes) porCodigo.set(c.codigo, c);
  }

  return (
    <section className="mt-5" aria-labelledby="titulo-favoritos">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 id="titulo-favoritos" className="font-display text-lg font-bold text-titulo">
            Seus favoritos
          </h2>
          <p className="mt-1 text-sm text-cinza">
            {favoritos.length === 1 ? "1 item guardado" : `${favoritos.length} itens guardados`},
            do mais recente para o mais antigo.
          </p>
        </div>
        <Link to={`${BASE}${window.location.search}`} className="text-sm font-semibold text-link hover:underline">
          voltar à tabela
        </Link>
      </div>

      <ul className="mt-4 flex flex-col gap-3">
        {favoritos.map((f) => (
          <LinhaDeFavorito
            key={f.id}
            f={f}
            classificacao={f.cclasstrib ? porCodigo.get(f.cclasstrib) ?? null : null}
            nomeDoCst={nomeDoCst.get(f.cst) ?? null}
            doTributoDaTela={f.tributo === tributo}
            salvando={salvando === chaveDoFavorito(f.tributo, f.cst, f.cclasstrib)}
            comErro={erroEm === chaveDoFavorito(f.tributo, f.cst, f.cclasstrib)}
            aoRemover={() => aoAlternar(f.tributo, f.cst, f.cclasstrib)}
            aoAnotar={(nota) => aoAnotar(f.id, nota)}
          />
        ))}
      </ul>
    </section>
  );
}

function LinhaDeFavorito({
  f,
  classificacao,
  nomeDoCst,
  doTributoDaTela,
  salvando,
  comErro,
  aoRemover,
  aoAnotar,
}: {
  f: Favorito;
  classificacao: Classificacao | null;
  nomeDoCst: string | null;
  doTributoDaTela: boolean;
  salvando: boolean;
  comErro: boolean;
  aoRemover: () => void;
  aoAnotar: (nota: string) => Promise<boolean>;
}) {
  const [nota, setNota] = useState(f.nota ?? "");
  const [editando, setEditando] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [erroNota, setErroNota] = useState(false);

  const nomeDoTributo = TRIBUTOS.find((t) => t.id === f.tributo)?.nome ?? f.tributo;
  const alvo = `${BASE}?${new URLSearchParams({
    ...(f.tributo === "is" ? { t: "is" } : {}),
    ...(f.cclasstrib ? { cl: f.cclasstrib } : {}),
  }).toString()}`;

  return (
    <li className="rounded-cf-lg border border-borda bg-superficie p-4" data-favorito={f.id}>
      <div className="flex flex-wrap items-start gap-3">
        <Codigo grande>{f.cclasstrib ?? f.cst}</Codigo>
        <div className="min-w-0 flex-1">
          <p className="text-sm leading-snug text-tinta">
            {f.cclasstrib
              ? (classificacao?.descricao ??
                (doTributoDaTela
                  ? "Esta classificação não existe na vigência que está na cabine."
                  : `Guardada na tabela de ${nomeDoTributo}.`))
              : (nomeDoCst ??
                (doTributoDaTela
                  ? "Este CST não existe na vigência que está na cabine."
                  : `CST inteiro, na tabela de ${nomeDoTributo}.`))}
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-1.5">
            <Chip>{nomeDoTributo}</Chip>
            {/* "CST inteiro" é um favorito legítimo, e a linha DIZ isso: sem o
                chip, um código de três dígitos ao lado de outros de seis parece
                um erro de digitação. */}
            {!f.cclasstrib && <Chip tom="acento">CST inteiro</Chip>}
            <Chip>CST {f.cst}</Chip>
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Link
            to={alvo}
            className="rounded-lg border border-borda px-3 py-1.5 text-xs font-semibold text-tinta transition hover:border-laranja"
          >
            Abrir
          </Link>
          <button
            type="button"
            disabled={salvando}
            onClick={aoRemover}
            className="rounded-lg border border-borda px-3 py-1.5 text-xs font-semibold text-cinza transition hover:border-erro-tinta hover:text-erro-tinta disabled:opacity-50"
          >
            {salvando ? "Removendo…" : "Remover"}
          </button>
        </div>
      </div>

      {comErro && (
        <p className="mt-2 text-xs text-erro-tinta">
          Não conseguimos remover agora. Tente de novo — os outros favoritos continuam aqui.
        </p>
      )}

      <div className="mt-3 border-t border-borda pt-3">
        {editando ? (
          <div className="flex flex-col gap-2">
            <textarea
              id={`nota-${f.id}`}
              className="min-h-[4.5rem] w-full rounded-xl border-[1.5px] border-borda bg-superficie px-3 py-2 text-sm text-tinta placeholder:text-cinza/60 focus:border-laranja focus:ring-3 focus:ring-laranja/20 focus:outline-none"
              maxLength={500}
              autoFocus
              placeholder="Onde você usa esta classificação no dia a dia…"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
            />
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={gravando}
                onClick={async () => {
                  setGravando(true);
                  setErroNota(false);
                  const ok = await aoAnotar(nota);
                  setGravando(false);
                  if (ok) setEditando(false);
                  else setErroNota(true);
                }}
                className="pill-cta px-4 py-1.5 text-xs disabled:opacity-50"
              >
                {gravando ? "Salvando…" : "Salvar anotação"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setNota(f.nota ?? "");
                  setEditando(false);
                  setErroNota(false);
                }}
                className="text-xs font-semibold text-cinza hover:underline"
              >
                cancelar
              </button>
              {/* Apagar é ESVAZIAR o campo e salvar — e a tela diz isso, em vez
                  de esconder a ação atrás de um botão a mais. */}
              <span className="text-xs text-cinza">
                Deixe em branco para apagar a anotação.
              </span>
            </div>
            {erroNota && (
              <p className="text-xs text-erro-tinta">
                Não conseguimos salvar a anotação agora. O favorito continua guardado.
              </p>
            )}
          </div>
        ) : f.nota ? (
          <button
            type="button"
            onClick={() => setEditando(true)}
            className="w-full text-left"
          >
            <span className="block text-xs font-semibold text-cinza">Sua anotação</span>
            <span className="mt-0.5 block text-sm leading-relaxed whitespace-pre-line text-tinta">
              {f.nota}
            </span>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setEditando(true)}
            className="text-xs font-semibold text-link hover:underline"
          >
            + anotar
          </button>
        )}
      </div>
    </li>
  );
}
